/**
 * Converts Firebase authentication error codes to user-friendly messages.
 * @param {Error|string} error - The error object or error message from Firebase
 * @returns {string} A user-friendly error message
 */
export function getAuthErrorMessage(error) {
  // Extract error code from Firebase error message or error object
  let errorCode = '';
  
  if (typeof error === 'string') {
    errorCode = error;
  } else if (error?.code) {
    errorCode = error.code;
  } else if (error?.message) {
    // Firebase errors often come as "Firebase: Error (auth/error-code)."
    const match = error.message.match(/\(auth\/([^)]+)\)/);
    if (match) {
      errorCode = `auth/${match[1]}`;
    } else {
      errorCode = error.message;
    }
  }

  // Map Firebase error codes to user-friendly messages
  const errorMessages = {
    // Sign-in errors
    'auth/user-not-found': 'Invalid email or password. Please try again.',
    'auth/wrong-password': 'Invalid email or password. Please try again.',
    'auth/invalid-credential': 'Invalid email or password. Please try again.',
    'auth/invalid-login-credentials': 'Invalid email or password. Please try again.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/user-disabled': 'This account has been disabled. Please contact support.',
    'auth/too-many-requests': 'Too many attempts. Please wait a few minutes and try again.',
    'auth/missing-password': 'Please enter your password.',
    'auth/missing-email': 'Please enter your email address.',
    
    // Account creation errors
    'auth/email-already-in-use': 'This email is already registered. Please sign in instead.',
    'auth/weak-password': 'Password must be at least 6 characters.',
    'auth/operation-not-allowed': 'This sign-in method is not available. Please contact support.',
    
    // Password reset errors
    'auth/expired-action-code': 'This reset link has expired. Please request a new one.',
    'auth/invalid-action-code': 'This reset link is invalid. Please request a new one.',
    
    // Network errors
    'auth/network-request-failed': 'Connection failed. Please check your internet and try again.',
    
    // Session errors
    'auth/requires-recent-login': 'Please log out and log in again to continue.',
    'auth/session-expired': 'Your session has expired. Please log in again.',
    
    // General errors
    'auth/internal-error': 'Something went wrong. Please try again later.',
    'auth/invalid-api-key': 'Configuration error. Please contact support.',
  };

  // Return the user-friendly message or a generic fallback
  return errorMessages[errorCode] || 
    'Invalid email or password. Please try again.';
}

export default getAuthErrorMessage;

